// Helper to check if element or any parent is fixed/sticky
export function hasFixedOrStickyParent(element: HTMLElement): {
  isFixed: boolean;
  isSticky: boolean;
  fixedParent: HTMLElement | null;
} {
  let current: HTMLElement | null = element;
  let hasFixed = false;
  let hasSticky = false;
  let fixedParentElement: HTMLElement | null = null;

  while (current && current !== document.body) {
    const computedStyle = window.getComputedStyle(current);
    const position = computedStyle.position;

    if (position === 'fixed') {
      hasFixed = true;
      if (!fixedParentElement) fixedParentElement = current;
    }
    if (position === 'sticky' || position === '-webkit-sticky') {
      hasSticky = true;
    }

    current = current.parentElement;
  }

  return { isFixed: hasFixed, isSticky: hasSticky, fixedParent: fixedParentElement };
}

// Returns the id of the currently selected tab in the tablist that holds the tour's tab steps.
// Used to remember which tab a step without its own `tabId` was first shown under.
export function getSelectedTabId(tabIds: string[]): string | null {
  for (const tabId of tabIds) {
    try {
      const selector = tabId.startsWith('#') || tabId.startsWith('.') ? tabId : `#${tabId}`;
      const tabList = document.querySelector(selector)?.closest('[role="tablist"]');
      const selected = tabList?.querySelector<HTMLElement>(
        '[role="tab"][aria-selected="true"]',
      );
      if (selected?.id) return selected.id;
    } catch {
      // ignore invalid selector
    }
  }
  return null;
}

// Restores an element's inline `position`/`top`/`bottom` after the tour force-disabled it
// (see the sticky/fixed handling in `scrollToCenter` and in useTour's mobile positioning) so a
// header/sidebar/sticky CTA the tour touched doesn't stay `position: static` for the rest of
// the page's life. No-op if the element was never disabled.
export function restoreDisabledPosition(element: HTMLElement) {
  if (!element.hasAttribute('data-tour-original-position-inline')) return;

  const originalPosition = element.getAttribute('data-tour-original-position-inline') || '';
  const originalTop = element.getAttribute('data-tour-original-top-inline') || '';
  const originalBottom = element.getAttribute('data-tour-original-bottom-inline') || '';

  // Remove the important flag and restore original inline styles
  element.style.removeProperty('position');
  element.style.removeProperty('top');
  element.style.removeProperty('bottom');

  // Restore original inline styles (empty string means no inline style)
  if (originalPosition) {
    element.style.position = originalPosition;
  }
  if (originalTop) {
    element.style.top = originalTop;
  }
  if (originalBottom) {
    element.style.bottom = originalBottom;
  }

  // Clean up data attributes
  element.removeAttribute('data-tour-original-position-inline');
  element.removeAttribute('data-tour-original-top-inline');
  element.removeAttribute('data-tour-original-bottom-inline');
}

// Restores every element the tour disabled during this run. Elements are found by the same
// `data-tour-original-position-inline` marker `hasDisabledParent` checks for, so this catches
// every disable site (scrollToCenter, the first-step fixed-parent shortcut, the non-tab step
// positioning branch) without needing each call site to report back individually.
export function restoreAllDisabledPositions() {
  if (typeof document === 'undefined') return;

  document
    .querySelectorAll<HTMLElement>('[data-tour-original-position-inline]')
    .forEach((element) => restoreDisabledPosition(element));
}

// Helper to check if element or any parent was disabled (has tour attribute)
export function hasDisabledParent(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;

  while (current && current !== document.body) {
    if (current.hasAttribute('data-tour-original-position-inline')) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
}

// Shared visibility helper used in several places
export function isElementVisibleStrict(el: HTMLElement | null): boolean {
  if (!el) return false;
  try {
    const style = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      rect.width > 0 &&
      rect.height > 0
    );
  } catch {
    return false;
  }
}

// Function to find the first visible element among all matching elements
// For elements with tabId, we always create a placeholder that will later be
// redirected to the real element once the tab content is activated.
export function findVisibleElement(
  selector: string,
  hasTabId: boolean = false,
): Element | null {
  if (typeof window === 'undefined') return null;

  // For elements that are inside a tab (hasTabId === true), we always create a placeholder so that driver.js can focus on it before rendering the tab content.
  // Later in onHighlightStarted we will connect this placeholder to the actual element.
  if (hasTabId) {
    const placeholder = document.createElement('div');
    placeholder.id = `tour-placeholder-${selector.replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}`;
    placeholder.setAttribute('data-tour-placeholder', selector);
    // Position at center of viewport so popover shows in center
    // Use 1x1 size to minimize visual impact
    placeholder.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        width: 1px;
        height: 1px;
        transform: translate(-50%, -50%);
        opacity: 0;
        pointer-events: none;
        z-index: -1;
      `;
    document.body.appendChild(placeholder);
    return placeholder;
  }
  //For steps that don't have a tabId
  // Use querySelectorAll to get ALL matching elements
  const elements = document.querySelectorAll(selector);

  if (elements.length === 0) return null;

  // If element has a tabId, return the first element even if not visible
  // because it will become visible when the tour activates its tab
  if (hasTabId && elements.length > 0) {
    return elements[0];
  }

  // Find the first visible element
  // Helper to detect loading/skeleton placeholders so we avoid anchoring tour to them
  const isLoadingElement = (el: Element) => {
    try {
      if (!(el instanceof HTMLElement)) return false;
      const cls = (el.className || '').toString();
      const loadingRegex = /(loading|spinner|skeleton|shimmer|placeholder|is-loading)/i;
      if (loadingRegex.test(cls)) return true;
      // aria-busy or data-loading attributes
      if (el.getAttribute('aria-busy') === 'true') return true;
      if (el.getAttribute('data-loading') === 'true') return true;
      // descendant spinner
      if (
        el.querySelector &&
        el.querySelector('.spinner, .loading, .skeleton, [data-loading]')
      )
        return true;
    } catch {
      // ignore
    }
    return false;
  };

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i] as Element;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    // Check if element is visible (not hidden by CSS and has dimensions)
    const isVisible =
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      rect.width > 0 &&
      rect.height > 0;

    if (!isVisible) continue;

    // Avoid anchoring to obvious loading placeholders. If element is loading, try next match.
    if (isLoadingElement(element)) continue;

    return element;
  }

  return null;
}

// Function to activate a tab if needed
export async function activateTab(tabId: string): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    // Add '#' prefix if not present
    const tabSelector = tabId.startsWith('#') || tabId.startsWith('.') ? tabId : `#${tabId}`;
    const tabElement = document.querySelector(tabSelector);

    if (!tabElement) {
      // eslint-disable-next-line no-console
      console.warn(`Tab element not found: ${tabSelector}`);
      return false;
    }

    // Try to click the tab to activate it
    if (tabElement instanceof HTMLElement) {
      // First, scroll the tab into view (important for mobile horizontal scroll)
      // tabElement.scrollIntoView({
      //   behavior: 'smooth',
      //   block: 'nearest',
      //   inline: 'center',
      // });

      // Wait a bit for scroll to complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Set a flag to indicate this click is from the tour
      // This prevents URL changes that would reload the page
      (window as any).__tourTabClick = true;
      tabElement.click();

      // Reset the flag after a short delay
      await new Promise((resolve) => setTimeout(resolve, 100));
      (window as any).__tourTabClick = false;

      return true;
    }
  } catch {
    // ignore
  }

  return false;
}

export const showPopover = (popover: Element, adjustArrow?: () => void) => {
  try {
    const el = popover as HTMLElement;
    const activeElement = document.querySelector('.driver-active-element') as HTMLElement;

    // Reset animation state completely for popover and highlight to prevent jump/flicker
    // Note: We don't touch overlay - it should stay visible and animate smoothly
    el.style.transition = 'none';
    el.style.opacity = '0';

    if (activeElement) {
      activeElement.style.transition = 'none';
    }

    // Force reflow
    el.offsetHeight;
    if (activeElement) activeElement.offsetHeight;

    // Reset any manual hiding styles to let driver.js handle animation
    el.style.removeProperty('opacity');
    el.style.removeProperty('visibility');
    el.style.removeProperty('pointer-events');
    el.style.removeProperty('transition');

    if (activeElement) {
      activeElement.style.removeProperty('transition');
    }

    // Adjust arrow position after popover is visible
    if (adjustArrow) {
      // Use requestAnimationFrame for smoother positioning
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            adjustArrow();
          } catch {}
        });
      });
    }
  } catch {
    // ignore
  }
};

// Helper to wait for driver popover element to be added to DOM
export async function waitForPopoverElement(timeout = 1000): Promise<Element | null> {
  if (typeof document === 'undefined') return null;
  const start = Date.now();
  let pop = document.querySelector('.driver-popover');
  while (!pop && Date.now() - start < timeout) {
    // small delay
    await new Promise((r) => setTimeout(r, 50));
    pop = document.querySelector('.driver-popover');
  }
  return pop;
}

// Helper function to scroll element into viewport with proper spacing for popover
export async function scrollToCenter(element: HTMLElement, isTabElement: boolean = false) {
  try {
    const isMobile = window.innerWidth <= 768;
    const minTopOffset = isMobile ? 80 : 100; // Minimum distance from top of viewport
    const minBottomOffset = isMobile ? 220 : 100; // Minimum distance from bottom

    // Check if element or any parent is sticky or fixed
    const computedStyle = window.getComputedStyle(element);
    const position = computedStyle.position;
    const elementIsSticky = position === 'sticky' || position === '-webkit-sticky';
    const elementIsFixed = position === 'fixed';

    // Also check parents
    const parentCheck = hasFixedOrStickyParent(element);
    const isSticky = elementIsSticky || parentCheck.isSticky;
    const isFixed = elementIsFixed || parentCheck.isFixed;
    const fixedParent = parentCheck.fixedParent;

    // If element is fixed or sticky, don't scroll - it's already in view
    // if (isFixed) {
    //   // Fixed elements are always in viewport, no need to scroll
    //   return;
    // }

    if (isMobile) {
      // Check if element or parent was already disabled by tour
      const alreadyDisabled = hasDisabledParent(element);

      // If already disabled (fixed/sticky parent), don't scroll again
      if (alreadyDisabled) {
        return; // Skip scrolling for elements that were already processed
      }

      // For sticky elements, we need to find their natural position in the DOM
      // Then scroll to center them (which will unstick them)
      let absoluteElementTop: number;
      let elementHeight: number;
      const viewportHeight = window.innerHeight;

      if (isSticky || isFixed) {
        // Determine which element to disable (element itself or fixed parent)
        const elementToDisable = fixedParent || element;

        // Disable sticky/fixed for the duration of the tour
        // Store original values to restore later (only once per element)
        if (!elementToDisable.hasAttribute('data-tour-original-position-inline')) {
          // Store inline style values (to restore exactly what was there)
          elementToDisable.setAttribute(
            'data-tour-original-position-inline',
            elementToDisable.style.position || '',
          );
          elementToDisable.setAttribute(
            'data-tour-original-top-inline',
            elementToDisable.style.top || '',
          );
          elementToDisable.setAttribute(
            'data-tour-original-bottom-inline',
            elementToDisable.style.bottom || '',
          );

          // Disable sticky/fixed permanently (for tour duration)
          elementToDisable.style.setProperty('position', 'static', 'important');

          // Force a reflow to get the actual position
          element.offsetHeight; // Force reflow
        }

        // For elements with fixed parent, don't scroll - just disable parent and return
        // Driver.js will handle positioning the popover correctly
        if (fixedParent) {
          return; // Skip scrolling for elements inside fixed containers
        }

        const naturalRect = element.getBoundingClientRect();
        absoluteElementTop = window.pageYOffset + naturalRect.top;
        elementHeight = naturalRect.height;
      } else {
        // For non-sticky elements, get position normally
        const elementRect = element.getBoundingClientRect();
        absoluteElementTop = window.pageYOffset + elementRect.top;
        elementHeight = elementRect.height;
      }

      // Position element exactly in the center (50% of viewport)
      const targetPosition = viewportHeight / 2;
      const scrollToPosition = absoluteElementTop - targetPosition + elementHeight / 2;

      // Use direct DOM manipulation for more reliable scrolling
      const scrollTop = Math.max(0, scrollToPosition);

      // Try multiple methods to ensure scrolling works
      window.scrollTo({
        top: scrollTop,
        behavior: 'auto',
      });

      // Fallback: direct property assignment
      document.documentElement.scrollTop = scrollTop;
      document.body.scrollTop = scrollTop;

      await new Promise((r) => setTimeout(r, 100));

      // Verify scroll happened with multiple retries
      for (let retry = 0; retry < 3; retry++) {
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
        const scrollDiff = Math.abs(currentScroll - scrollTop);

        if (scrollDiff > 30) {
          // Force scroll again
          window.scrollTo({
            top: scrollTop,
            behavior: 'auto',
          });
          document.documentElement.scrollTop = scrollTop;
          document.body.scrollTop = scrollTop;
          await new Promise((r) => setTimeout(r, 50));
        } else {
          // Scroll is accurate enough
          break;
        }
      }
    } else {
      // For desktop, use scrollIntoView with center alignment
      element.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'nearest',
      });

      await new Promise((r) => setTimeout(r, 50));
    }

    // For desktop only, verify element is in viewport and adjust if needed
    if (!isMobile) {
      const elementRect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Adjust if too close to top
      if (elementRect.top < minTopOffset) {
        const scrollAdjustment = minTopOffset - elementRect.top;
        const currentScroll = window.pageYOffset || window.scrollY;

        const newScrollTop = currentScroll - scrollAdjustment;
        document.documentElement.scrollTop = newScrollTop;
        document.body.scrollTop = newScrollTop;

        await new Promise((r) => setTimeout(r, 25));
      }

      // Adjust if too close to bottom
      if (elementRect.bottom > viewportHeight - minBottomOffset) {
        const scrollAdjustment = elementRect.bottom - (viewportHeight - minBottomOffset);
        const currentScroll = window.pageYOffset || window.scrollY;

        const newScrollTop = currentScroll + scrollAdjustment;
        document.documentElement.scrollTop = newScrollTop;
        document.body.scrollTop = newScrollTop;

        await new Promise((r) => setTimeout(r, 25));
      }
    }
  } catch (e) {
    // Ignore scroll errors
  }
}
